import type { SidebarCount } from '@skupreme/api-contracts/core';
import { CommonUtility } from '@utility/common';
import { APIPath } from '@utility/constants/api';
import { CacheUtility } from '@utility/cache';
import { BaseService } from './base';
import { CRUDService } from './crud';

class Organizations extends CRUDService {
  constructor() {
    super(APIPath.organizations);
  }

  settings = async (forceRefresh = false) => {
    if (!forceRefresh) {
      const res = CacheUtility.get(APIPath.organizationsSettings);
      if (res) {
        return res;
      }
    }

    const res = await BaseService.get(APIPath.organizationsSettings);
    CacheUtility.save(APIPath.organizationsSettings, res);
    return res;
  };

  /* `fresh` asks the API to skip its 60 s sidebar cache, after the user changed something. */
  sidebarCount = (signal?: AbortSignal, fresh = false) =>
    BaseService.get<SidebarCount>(
      APIPath.sidebarCount,
      false,
      true,
      signal,
      undefined,
      fresh ? { 'Cache-Control': 'no-cache' } : undefined,
    );

  keys = () => BaseService.get(APIPath.organizationsKeys);

  updateOrgKeys(payload) {
    return BaseService.put(APIPath.organizationsKeys, payload);
  }

  logs = filter => {
    const params = CommonUtility.objectToParams(filter);
    return BaseService.get(`${APIPath.organizationsLogs}/?${params}`);
  };

  logTypes = () => BaseService.get(APIPath.organizationsLogTypes);

  walmartLog = feed_id => BaseService.post(APIPath.walmartLog, { feed_id });

  refreshLog = () => BaseService.put(APIPath.organizationsLogs);

  logoSignedUrl(params) {
    const url = `${APIPath.organizationsLogoUrl}?${CommonUtility.objectToParams(
      params,
    )}`;
    return BaseService.get(url);
  }

  refreshGoogleSheets = type =>
    BaseService.get([APIPath.googleSheets, type].join('/'));

  fetchLogsSheets = filter => {
    const params = CommonUtility.objectToParams(filter);
    return BaseService.get(`${APIPath.logsSheets}/?${params}`);
  };

  transferOwnership = data => BaseService.put(APIPath.organizationsOwner, data);

  updateOrganisation = data => BaseService.put(APIPath.organizations, data);

  fetchGlobalSearch = search =>
    BaseService.post(`${APIPath.globalSearch}?limit=30`, { search });

  getTaxMap = () => BaseService.get(APIPath.organizationsTaxCodes);

  addOrganizationTaxField = payload =>
    BaseService.post(APIPath.organizationsTaxCodes, payload);

  organizationTaxCodeConfig = () =>
    BaseService.get(APIPath.organizationsTaxCodesConfig);

  updateTaxStatus = (regionId, taxType, payload) =>
    BaseService.put(
      `${APIPath.organizationsTaxCodes}/${regionId}/${taxType}/status`,
      payload,
    );

  deleteOrganizationTaxField = (regionId, taxType) =>
    BaseService.remove(
      `${APIPath.organizationsTaxCodes}/${regionId}/${taxType}`,
    );
}

const OrganizationsService = new Organizations();
Object.freeze(OrganizationsService);
export default OrganizationsService;
